import React from 'react';
import { Button } from '../atoms/button';

/**
 * RedirectTemplate Props
 * 
 * Template component for the redirect/authentication loading page.
 * Shows a success state after authentication is complete.
 */
export interface RedirectTemplateProps {
    /** Whether this is the web platform (affects sizing) */
    isWeb?: boolean;

    /** User email to display */
    userEmail?: string;

    /** Callback when redirect button is clicked */
    onRedirect?: () => void;

    /** Whether pairing is in progress */
    isPairing?: boolean;

    /** Error message from pairing */
    pairingError?: string | null;

    /** Whether the redirect button is disabled */
    isRedirectDisabled?: boolean;
}

/**
 * RedirectTemplate
 * 
 * Redirect/success page template shown after authentication flow completes.
 * Displays success message and redirect button back to Figma.
 * 
 * Usage:
 * ```tsx
 * <RedirectTemplate
 *   userEmail="user@example.com"
 *   onRedirect={() => window.close()}
 *   isWeb={true}
 * />
 * ```
 */
export const RedirectTemplate: React.FC<RedirectTemplateProps> = ({
    isWeb = true,
    userEmail = '',
    onRedirect,
    isPairing = false,
    pairingError = null,
    isRedirectDisabled = false,
}) => {
    return (
        <div className="w-full flex flex-col items-center justify-center min-h-[400px] py-12">
            {/* Welcome Heading */}
            <h1 className="text-5xl font-bold text-gray-900 mb-8">
                Welcome aboard!
            </h1>

            {/* Icons Row */}
            <div className="flex items-center gap-6 mb-8">
                {/* Ideoz Icon */}
                <div className="w-16 h-16 flex items-center justify-center">
                    <img
                        src="https://bxo.ezv.temporary.site/website_f5e29595/wp-content/themes/ideoz-wordpress/src/images/logo-id.svg"
                        alt="Ideoz"
                        className="w-full h-full"
                    />
                </div>

                {/* Repeat/Sync Icon */}
                <div className="w-12 h-12 flex items-center justify-center text-gray-400">
                    <img
                        src="https://bxo.ezv.temporary.site/website_f5e29595/wp-content/themes/ideoz-wordpress/src/images/fi_repeat.svg"
                        alt="Connected"
                        className={isPairing ? 'w-full h-full animate-spin' : 'w-full h-full'}
                    />
                </div>

                {/* Figma Icon */}
                <div className="w-16 h-16 flex items-center justify-center">
                    <img
                        src="https://bxo.ezv.temporary.site/website_f5e29595/wp-content/themes/ideoz-wordpress/src/images/figma.svg"
                        alt="Figma"
                        className="w-full h-full"
                    />
                </div>
            </div>

            {/* Status Message */}
            <div className="text-center max-w-md mb-4">
                {isPairing ? (
                    <p className="text-lg text-gray-700 mb-2">
                        Connecting your account to Figma...
                    </p>
                ) : (
                    <>
                        <p className="text-lg text-gray-700 mb-2">
                            Your login is complete and Figma is connected{userEmail ? ` with ${userEmail}` : ''}.
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                            Get started with Ideoz plugins now
                        </p>
                    </>
                )}
            </div>

            {/* Error Message */}
            {pairingError && (
                <div className="text-center max-w-md mb-4">
                    <p className="text-sm text-red-600">
                        {pairingError}
                    </p>
                </div>
            )}

            {/* Redirect Button */}
            <Button
                variant="primary"
                size={isWeb ? 'xl' : 'lg'}
                onClick={onRedirect}
                disabled={isPairing || isRedirectDisabled}
                className="mt-6 px-16 bg-[#3E4A6B] hover:bg-[#2E3A5B] disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isPairing ? 'Connecting...' : 'Redirect to Figma'}
            </Button>
        </div>
    );
};
